import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { adminService, userService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button, Select } from '../../../atoms'
import { EditTable } from '../../../organisms'
import AdminLayoutIndex from '../_layouts'
import * as constants from '../../../../static/constants'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  users: {
    users: IJsonResponse
    meta: interfaces.IPager
  }
  csv_path: string
}

const COLUMNS = [
  {
    name: I18n.t('generic.id'),
    field: 'id',
    required: false,
    readOnly: true,
    link: record => `/admin/users/${record.id}/edit`,
  },
  {
    name: I18n.t('generic.email'),
    field: 'email',
    required: true,
  },
  {
    name: I18n.t('session.user_type'),
    field: 'user_type',
    required: true,
  },
  {
    name: I18n.t('generic.username'),
    field: 'username',
    required: false,
  },
  {
    name: I18n.t('generic.identification'),
    field: 'identification',
    required: false,
    renderElement: record => (
      <Identif onClick={() => showID(record.identification_images_urls)}>
        {record.identification_images_urls.length > 0 && (
          <>
            <img width="50" src={`${record.identification_images_urls[0]}`} className="thumbnail" />
            {record.identification.workflow_state === 'approved' && (
              <span className="green">&#9675;</span>
            )}
            {record.identification.workflow_state === 'requested' && (
              <span className="red">&#10067;</span>
            )}
            {record.identification.workflow_state === 'declined' && (
              <span className="red">&#10005;</span>
            )}
          </>
        )}
      </Identif>
    ),
  },
]

const showID = urls => {
  const ModalBody: React.FC<{}> = modalBodyProps => {
    return (
      <div>{urls.length > 0 && urls.map(url => <img width="400" src={`${url}`} key={url} />)}</div>
    )
  }
  window.globalModal.showModal({
    title: 'Uploaded Documents :',
    body: <ModalBody />,
    closeText: I18n.t('generic.close'),
  })
}

const urlParams = new URLSearchParams(window.location.search)

const searchUsers = async baseParams => {
  const keywords = urlParams.get('keywords')
  const params = { ...baseParams }

  if (keywords) {
    params.keywords = keywords
  }

  return adminService.searchUsers(params)
}

const AdminUserIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('Admin users')

  const { data } = adminService.getDataFromJson(props.users.users)
  const [users, setUsers] = React.useState(data)
  const { pagination: initialPagination } = React.useMemo(
    () => userService.getUsersFromJson(props.users.users),
    []
  )
  const [loading, setLoading] = React.useState(false)
  const [pagination, setPagination] = React.useState(initialPagination)

  const [keywords, setKeywords] = React.useState('')
  const [role, setRole] = React.useState('all')

  const getUsersByPage = React.useCallback(async page => {
    setLoading(true)
    const currentSearchParams = new URLSearchParams(window.location.search)
    const filterParams = {
      keywords: newKeyword.current,
      role: newRole.current,
    }
    const result = await searchUsers({ ...filterParams, page })
    setUsers(result.users)
    setPagination(result.pagination)
    setLoading(false)
  }, [])

  const handleClickDelete = record => {
    if (window.confirm(I18n.t('generic.delete_confirmation'))) {
      handleDelete(record)
    }
  }
  const handleDelete = React.useCallback(
    async id => {
      const { flush } = await adminService.deleteUser(id)
      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      setUsers(users.filter(user => user.id !== id))
    },
    [users]
  )

  const defaultKeywords = urlParams.get('keywords') || ''
  const defaultRole = urlParams.get('role') || 'all'
  const options = [
    { id: 'all', name: I18n.t('generic.everyone') },
    { id: 'guest', name: I18n.t('generic.guest') },
    { id: 'guide', name: I18n.t('generic.guide') },
    { id: 'agent', name: I18n.t('generic.agent') },
    { id: 'administrator', name: I18n.t('generic.administrator') },
  ]

  const formatSelectOptions = newoptions => {
    return newoptions.map((option: { id: number; name: string }) => ({
      value: option.id,
      label: option.name,
    }))
  }

  const onKeywordHandler = event => {
    setKeywords(event.target.value)
  }

  const onRoleHandler = event => {
    setRole(event.target.value)
  }

  const onSearchHandler = async event => {
    const filterParams = {
      keywords: newKeyword.current,
      role: newRole.current,
    }

    const result = await searchUsers({ ...filterParams })

    setUsers(result.users)
    setPagination(result.pagination)
    setLoading(false)
    history.replaceState(
      null,
      null,
      `/admin/users/search?keywords=${filterParams.keywords}&role=${filterParams.role}`
    )
  }

  const newKeyword = React.useRef(keywords)
  React.useEffect(() => {
    newKeyword.current = keywords
  }, [keywords])

  const newRole = React.useRef(role)
  React.useEffect(() => {
    newRole.current = role
  }, [role])

  const updatedUsers = React.useRef(users)
  React.useEffect(() => {
    updatedUsers.current = users
  }, [users])

  const renderRecordControls = React.useCallback(
    record => (
      <>
        {record.status === 'active' && (
          <Button key="status" small={true} handleClick={() => handleClickInactivate(record.id)}>
            {I18n.t('generic.deactivate')}
          </Button>
        )}
        {record.status === 'inactive' && (
          <Button
            textColor="red"
            key="status"
            small={true}
            handleClick={() => handleClickActivate(record.id)}
          >
            {I18n.t('generic.activate')}
          </Button>
        )}
        {record.has_relations === true && (
          <Button key="relations" small={true} handleClick={() => showRelationsModal(record.id)}>
            Relations
          </Button>
        )}
      </>
    ),
    []
  )

  const handleClickInactivate = record => {
    if (window.confirm(I18n.t('generic.inactivate_confirmation'))) {
      handleInactivate(record)
    }
  }
  const handleInactivate = React.useCallback(
    async id => {
      const { user, flush } = await adminService.inactivateUser(id)
      const elementsIndex = updatedUsers.current.findIndex(element => element.id === id)
      const newUsers = [...updatedUsers.current]
      newUsers[elementsIndex] = { ...newUsers[elementsIndex], status: user.data.attributes.status }

      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      setUsers(newUsers)
    },
    [users]
  )
  const handleClickActivate = record => {
    if (window.confirm(I18n.t('generic.activate_confirmation'))) {
      handleActivate(record)
    }
  }
  const handleActivate = React.useCallback(
    async id => {
      const { user, flush } = await adminService.activateUser(id)
      const elementsIndex = updatedUsers.current.findIndex(element => element.id === id)
      const newUsers = [...updatedUsers.current]
      newUsers[elementsIndex] = { ...newUsers[elementsIndex], status: user.data.attributes.status }

      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      setUsers(newUsers)
    },
    [users]
  )

  const Relation = user => {
    return (
      <p>
        <a href={`/admin/users/${user.user.id}/edit`} target="_blank">
          {user.user.fullname}({user.user.user_type})
        </a>
      </p>
    )
  }

  const showRelationsModal = async id => {
    const { users: relations } = await adminService.getUserRelations(id)

    const ModalBody: React.FC<{}> = modalBodyProps => {
      return (
        <div>
          {relations.length > 0 && relations.map(user => <Relation key={user.id} user={user} />)}
        </div>
      )
    }
    window.globalModal.showModal({
      title: 'Relations with :',
      body: <ModalBody />,
      closeText: I18n.t('generic.close'),
    })
  }

  return (
    <AdminLayoutIndex
      model="user"
      title={
        <Heading>
          <span>{I18n.t('generic.users')}</span>
          <Button small={true}>
            <a href="/admin/users/new">{I18n.t('generic.create_new')}</a>
          </Button>
          {props.csv_path && (
            <Button small={true}>
              <a href={props.csv_path}>{I18n.t('generic.csv') + I18n.t('generic.download')}</a>
            </Button>
          )}
          <Search className="Search">
            <div className="Search_Input _keywords">
              <input
                name="keywords"
                type="text"
                placeholder={I18n.t('generic.username_email')}
                defaultValue={defaultKeywords}
                onBlur={onKeywordHandler}
                onKeyDown={onKeywordHandler}
              />
              <SvgIcon name="search" className="icon" ariaHidden={true} role="presentation" />
            </div>
            <RoleFilter>
              <Select
                name="role"
                options={formatSelectOptions(options)}
                defaultValue={defaultRole}
                onChangeHandler={onRoleHandler}
              />
            </RoleFilter>
            <div>
              <Button primary={true} handleClick={() => onSearchHandler(event)}>
                {I18n.t('generic.search')}
              </Button>
            </div>
            <div className="reset">
              <a href="/admin/users/">{I18n.t('generic.clear')}</a>
            </div>
          </Search>
        </Heading>
      }
      main={
        <EditTable
          isCustomResource={true}
          getResourceByPage={getUsersByPage}
          loading={loading}
          editable={true}
          columns={COLUMNS}
          records={users}
          pagination={pagination}
          handleDelete={handleClickDelete}
          getEditLink={({ id }) => `/admin/users/${id}/edit`}
          renderRecordControls={renderRecordControls}
        />
      }
    />
  )
}

const Heading = styled.div`
  .Button {
    margin-left: 12px;
  }
`

const Search = styled.div`
  @media all and (max-width: 640px) {
    flex-flow: column wrap;
    justify-content: space-around;
  }

  display: flex;
  margin-right: 24px;
  margin-top: 10px;

  .reset {
    padding-top: 4px;
    a {
      font-size: 0.8em;
      line-height: 1.2em;
      text-decoration: underline;
      margin: 10px;
      color: ${constants.COLORS.primary};
    }
  }

  .Button {
    margin-left: 0;
    height: 38px;
    @media all and (max-width: 640px) {
      margin: 10px;
    }
  }

  .Search_Input {
    position: relative;

    @media all and (max-width: 640px) {
      margin: 10px;
    }

    > .icon {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 8px;
      margin: auto;
      width: 24px;
      height: 24px;
    }

    > input {
      display: block;
      width: 100%;
      padding: 8px 12px 8px 36px;
      border: solid 1px ${constants.COLORS.divider};
      border-radius: 4px;
      font-size: 15px;
      transition: border 0.2s ease;
      outline: none;
      background: none;

      &::placeholder {
        color: ${constants.COLORS.subText};
      }

      &:focus {
        border: solid 1px ${constants.COLORS.primary};
      }
    }
  }
`

const RoleFilter = styled.div`
  white-space: nowrap;
  margin-left: 10px;
  > div {
    display: inline-block;
  }
  select {
    padding: 7px !important;
    width: auto !important;
    margin-right: 20px !important;
  }
`

const Identif = styled.div`
  display: flex;
  align-items: center;
  .red,
  .green {
    margin: 0 10px;
  }
  .red {
    color: ${constants.COLORS.caution};
  }
  .green {
    color: ${constants.COLORS.success};
  }
`
export default AdminUserIndex
